import { Link, Navigate, Outlet, useLocation } from '@tanstack/react-router';
import { allowed, type Module } from '@buildmate/core';
import { useSession } from '../lib/session-context.tsx';

/**
 * The frame the company settings pages draw inside (P5-22).
 *
 * `costCodesRoute` carried this note from the day it was written: *"its own
 * top-level page under `/app` rather than a tab of a settings shell,
 * because there is one settings page so far and a shell with one tab in it
 * is a shell nobody needs yet. When the second arrives, this becomes a
 * child of a settings layout the way `/portal` just did."* There are now
 * **four**, and the note came due.
 *
 * Worse than the count was that they were reached from **two different
 * menus** — cost codes, catalog and tax rates behind *Financial*, safety
 * notices from a top-row *Safety* link — so somebody on the tax rates page
 * had no route to the safety notices except back up to the header.
 *
 * **This makes the header shorter, not longer.** That is the point rather
 * than a side effect: the main navigation has been one word away from
 * wrapping since Phase 5 began, and a two-line header has pushed the Gantt
 * chart below the fold and broken two drag tests three times. Folding two
 * entries into one buys that back.
 *
 * Each page keeps its **own** gate, and they deliberately differ — tax
 * rates on `invoices: view` rather than `settings`, because `settings` is
 * admin-only for every built-in role and would hide the rates from the
 * office staff who raise the invoices that use them. So the shell gates
 * nothing itself: it draws the links a person may follow and lets each
 * page refuse on its own terms. A shell that gated itself on one module
 * would hide three pages from the people who may use them.
 */
interface SettingsPage {
  readonly to: string;
  readonly label: string;
  /** What a role must hold to be offered it. See the note above. */
  readonly module: Module;
  readonly hint: string;
}

const PAGES: readonly SettingsPage[] = [
  {
    to: '/app/settings/cost-codes',
    label: 'Cost codes',
    module: 'costCodes',
    hint: 'The chart of accounts every job is costed against',
  },
  /*
    **Catalog and Stock are not here any more** (SCRUM-263). They moved
    to `/app/inventory`, a top-level entry, because Settings is where
    configuration lives and "how many connectors are left" is not a
    configuration question. The old URLs still redirect there.

    Their absence changes who is offered the Settings entry at all:
    `SETTINGS_MODULES` no longer contains `catalog`, so a role whose
    only settings-shaped module was the catalog now gets Inventory
    instead of a shell. That is the intended trade and the reason this
    comment is here rather than nothing.
  */
  {
    to: '/app/settings/tax-rates',
    label: 'Tax rates',
    // Not `settings`: that is admin-only for every built-in role, and the
    // people who raise the invoices these rates are charged on are office
    // staff. Hiding the rates from them would make the picker look broken.
    module: 'invoices',
    hint: 'Charged on owner invoices, and frozen onto each one',
  },
  {
    to: '/app/settings/safety-notices',
    label: 'Safety notices',
    module: 'settings',
    hint: 'What everybody has to read and acknowledge',
  },
  {
    to: '/app/settings/equipment',
    label: 'Equipment',
    // `equipment: view`, which the Field role holds: a crew looking up
    // which machine is meant to be on site today should find the page
    // rather than a shell that hides it.
    module: 'equipment',
    hint: 'The plant and tools this company owns',
  },
  {
    to: '/app/settings/checklists',
    label: 'Checklists',
    // `todos`, which the Field role holds: a crew applying the standard
    // checks has to be able to find the library. Changing one needs
    // `todos: admin`, which the page enforces and the rules decide.
    module: 'todos',
    hint: 'Standard checklists to add to a to-do',
  },
  {
    to: '/app/settings/assistant',
    label: 'Assistant',
    // `settings`, which is admin-only among the built-in roles: what the
    // assistant has cost is commercial, and the model is a company-wide
    // choice rather than a personal one.
    module: 'settings',
    hint: 'Which model answers, and what it has cost this month',
  },
];

/**
 * The modules that put a Settings entry in the header.
 *
 * Derived from `PAGES` rather than listed again. `AppLayout` carried its
 * own hand-written `costCodes || catalog || invoices || settings` test, and
 * adding the equipment page left a field user — who holds `equipment:
 * view` and nothing else here — able to reach `/app/settings` by URL while
 * the header refused to offer it. Two lists that have to agree is one list
 * too many, and this is the second time that shape has bitten in this
 * repository.
 */
export const SETTINGS_MODULES: readonly Module[] = PAGES.map((page) => page.module);

/**
 * `/app/settings` with nothing after it: go to the first page this role
 * allows.
 *
 * Which page that is depends on the session, which is why this is a
 * component rather than a redirect in `beforeLoad` — that runs before the
 * permissions are in hand. A role with none of the four gets the shell's
 * own explanation instead of being bounced somewhere it cannot read.
 */
export function SettingsIndex() {
  const { principal } = useSession();
  const first = PAGES.find((page) => (principal ? allowed(principal, page.module, 'view') : false));
  if (!first) return null;
  return <Navigate to={first.to} replace />;
}

export function SettingsLayout() {
  const { principal } = useSession();
  const { pathname } = useLocation();

  const shown = PAGES.filter((page) =>
    principal ? allowed(principal, page.module, 'view') : false,
  );

  return (
    <div className="flex flex-col gap-4 p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Settings</h1>
        <p className="mt-1 max-w-2xl text-sm text-slate-600">
          How this company works, shared by every job.
        </p>
      </div>

      {/*
        A `nav` of links rather than a tab control, like the portal's: each
        is a real address, so it can be bookmarked and sent to somebody.
        `aria-current` rather than a class alone, because "which page am I
        on" is the one thing a tab strip has to tell a screen reader.
      */}
      <nav aria-label="Settings" className="border-b border-slate-200">
        <ul className="-mb-px flex flex-wrap gap-1">
          {shown.map((page) => {
            const here = pathname.startsWith(page.to);
            return (
              <li key={page.to}>
                <Link
                  to={page.to}
                  title={page.hint}
                  aria-current={here ? 'page' : undefined}
                  className={
                    here
                      ? 'inline-block border-b-2 border-blue-600 px-3 py-2 text-sm font-medium text-blue-700'
                      : 'inline-block border-b-2 border-transparent px-3 py-2 text-sm text-slate-600 hover:border-slate-300 hover:text-slate-900'
                  }
                >
                  {page.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Nothing at all, which is a real state: a field user holds none of
          the four modules. Said rather than left as a bare heading over an
          empty strip. */}
      {shown.length === 0 && (
        <p className="rounded-lg bg-white p-5 text-sm text-slate-600 ring-1 ring-slate-200">
          Your role does not include any of the company settings. Ask an administrator if you need
          to change how something works.
        </p>
      )}

      <Outlet />
    </div>
  );
}
